// HaulerApp.js
// Standalone Hauler app shell for /haulers route surface.
// Renders independently of App.modular.
//
// Bucket A+B scope (Phase 1.6 + 1.8 + 2 + 2.5.x) per commit a189e9d's
// discipline statement. Surge, viability floor, band pill, score deltas,
// and snapshot-lock framing are out of scope.
//
// Step 4: lifecycle wired via JobDetailScreen + driverJobService.
// useDriverJobs lifted here so Jobs/Route/Earnings share one feed.
//
// Original IP — LoadDash Services LLC

import React, { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "./services/supabaseService";
import { useAuth } from "./hooks/useAuth";
import { useDriverJobs } from "./hooks/useDriverJobs";
import { useColorScheme } from "./hooks/useColorScheme";
import { claimJob, markEnRoute, markArrived } from "./services/driverJobService";
import JobsScreen from "./pages/hauler/JobsScreen";
import RouteScreen from "./pages/hauler/RouteScreen";
import EarningsScreen from "./pages/hauler/EarningsScreen";
import ProfileScreen from "./pages/hauler/ProfileScreen";
import JobDetailScreen from "./pages/hauler/JobDetailScreen";
import SharedLoginScreen from "./components/auth/SharedLoginScreen";
import HaulerTerms from "./legal/HaulerTerms";
import DriverAgreement from "./legal/DriverAgreement";
import CommunityAlertBanner from "./components/CommunityAlertBanner";
import { getActiveAlerts } from "./services/communityAlertsService";
import { EVENTS, flushPendingRoleContextEvents, trackRoleContextEvent } from "./analytics/roleContextTelemetry";
import "./styles/haulerTheme.css";

// Polling cadence for community alerts when the tab is foregrounded.
// 60s is fast enough that a freshly-published Tier 3/4 surfaces within
// a minute, slow enough that we're not hammering the DB. The
// visibilitychange listener also refetches on tab focus, so a Hauler
// returning to a backgrounded tab sees the latest immediately.
const ALERT_POLL_INTERVAL_MS = 60_000;

const SORA = `@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&display=swap');`;

// Hauler shell colors. Themed values are CSS-variable references so
// useColorScheme can swap dark/light at the document root. Brand
// accents stay constant.
const LD = {
  navy: "var(--hl-bg)",
  ocean: "var(--hl-nav-bg)",
  sky: "var(--hl-sky)",
  fog: "var(--hl-text)",
  green: "var(--hl-green)",
  amber: "var(--hl-amber)",
  red: "var(--hl-red)",
};

const TABS = [
  { key: "jobs",     label: "Jobs" },
  { key: "route",    label: "Route" },
  { key: "earnings", label: "Earnings" },
  { key: "profile",  label: "Profile" },
];

function NavIcon({ type, color }) {
  const common = { stroke: color, strokeWidth: 2, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" };
  if (type === "jobs") return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="6" y="3" width="12" height="18" rx="2" {...common} />
      <path d="M9 7h6M9 11h6M9 15h4" {...common} />
    </svg>
  );
  if (type === "route") return (
    <svg width="22" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5v15l5-3 6 3 5-3V2l-5 3-6-3-5 3Z" {...common} />
      <path d="M9 2v15M15 5v15" {...common} />
    </svg>
  );
  if (type === "earnings") return (
    <svg width="21" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="6" width="18" height="13" rx="2" {...common} />
      <path d="M3 10h18M7 15h4" {...common} />
    </svg>
  );
  return (
    <svg width="22" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 19h14l-1-6a6 6 0 0 0-12 0l-1 6Z" {...common} />
      <path d="M8 19h8M9 13h6" {...common} />
    </svg>
  );
}

export default function HaulerApp() {
  const [tab, setTab] = useState("jobs");
  const { session, role, isAdmin, authLoading, passwordRecovery, setPasswordRecovery } = useAuth();
  const [wrongSurface, setWrongSurface] = useState(false);
  const [driverProfile, setDriverProfile] = useState(undefined);
  const [termsAccepted, setTermsAccepted] = useState(undefined);
  const [icSigned, setIcSigned] = useState(undefined);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const colorScheme = useColorScheme();
  const roleContextFlushedRef = useRef(false);
  const userId = session?.user?.id || null;

  useEffect(() => {
    if (!userId) return;
    if (!roleContextFlushedRef.current) {
      roleContextFlushedRef.current = true;
      flushPendingRoleContextEvents(userId);
    }
    trackRoleContextEvent(EVENTS.DRIVER_PORTAL_VIEWED, userId, {
      intent: "hauler_interest",
      role_context: "hauler",
      surface: "hauler_app",
      page: "hauler_portal",
      tab,
    });
  }, [userId, tab]);

  // Identity scoping: a resident-role account cannot access /haulers.
  // When detected, sign them out and surface a clear "wrong surface"
  // message. Admins and drivers proceed unchanged. A null role means
  // role is still loading or the user has no role yet — fall through.
  useEffect(() => {
    if (session && role === "resident") {
      setWrongSurface(true);
      supabase.auth.signOut().catch(() => {});
    }
  }, [session, role]);

  // Driver profile (RPC; same profile authority as the retired shell).
  // undefined while loading, null when no profile yet, object once loaded.
  useEffect(() => {
    let cancelled = false;
    if (!session?.user?.id) {
      setDriverProfile(null);
      return () => { cancelled = true; };
    }
    setDriverProfile(undefined);
    supabase.rpc("get_driver_profile").then(({ data }) => {
      if (!cancelled) setDriverProfile(Array.isArray(data) ? data[0] || null : data || null);
    }).catch(() => { if (!cancelled) setDriverProfile(null); });
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  // Hauler Terms acceptance check. Fires whenever session changes.
  // undefined while loading, false if not accepted, true if accepted.
  // Fail-closed: if the RPC errors, force the gate. Hauler must accept.
  useEffect(() => {
    let cancelled = false;
    if (!session?.user?.id) {
      setTermsAccepted(undefined);
      return () => { cancelled = true; };
    }
    setTermsAccepted(undefined);
    supabase.rpc("has_accepted_hauler_terms").then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        console.error("[HaulerApp] hauler-terms check failed", error.message);
        setTermsAccepted(false);
        return;
      }
      setTermsAccepted(Boolean(data));
    });
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  // Independent Contractor Agreement signature check. Fires after Terms.
  // Same fail-closed shape: undefined while loading, true if signed, false
  // if not (or if the RPC errors).
  useEffect(() => {
    let cancelled = false;
    if (!session?.user?.id) {
      setIcSigned(undefined);
      return () => { cancelled = true; };
    }
    setIcSigned(undefined);
    supabase.rpc("has_signed_ic_agreement").then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        console.error("[HaulerApp] ic-agreement check failed", error.message);
        setIcSigned(false);
        return;
      }
      setIcSigned(Boolean(data));
    });
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  // Merge a patch from a profile-edit save into local state so screens
  // see the new value without a full RPC re-fetch.
  function handleProfileUpdated(patch) {
    if (!patch) return;
    setDriverProfile(prev => ({ ...(prev || {}), ...patch }));
  }

  // Lifted job feed — shared across screens. The feed is real-data only:
  // available, active, and completed rows all come from Supabase requests.
  const jobs = useDriverJobs({ session, driverProfile });

  // Community Alerts (Phase 2) — Hauler-side feed.
  //
  // Audience filter: only alerts whose audience includes 'driver' surface
  // here. Default audience for new alerts is ['resident', 'driver'] so most
  // operational alerts hit both surfaces; admins can target Hauler-only or
  // resident-only via the audience array if needed.
  //
  // Refresh strategy:
  //   - On mount
  //   - On visibilitychange (tab focus → tab returns to foreground)
  //   - On a 60s polling interval while the tab is visible
  //
  // Per-alert dismissal lives in the same localStorage namespace as the
  // resident surface (loaddash_dismissed_alert_<id>) so a dual-role user
  // who dismisses an alert in one app doesn't see it again in the other.
  const [alerts, setAlerts] = useState([]);
  const refreshAlerts = useCallback(async () => {
    try {
      const rows = await getActiveAlerts({ audience: "driver" });
      setAlerts(rows);
    } catch (_) { /* keep last-known on transient failure */ }
  }, []);
  useEffect(() => {
    refreshAlerts();
    const interval = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        refreshAlerts();
      }
    }, ALERT_POLL_INTERVAL_MS);
    function onVisibilityChange() {
      if (document.visibilityState === "visible") refreshAlerts();
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
    return () => {
      clearInterval(interval);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
  }, [refreshAlerts]);

  // Inline claim handler for JobCard (pool jobs only). Re-throws on
  // error so JobCard can render local error copy. On success, the
  // refetch removes the row from the available slice and adds it to
  // the claimed slice — no other state changes here.
  const handleInlineClaim = useCallback(async (job) => {
    if (!session?.user?.id) throw new Error("SIGN_IN_REQUIRED");
    await claimJob({ requestId: job.id, driverUserId: session.user.id, crewNames: [] });
    await jobs.refetch();
  }, [session?.user?.id, jobs.refetch]);

  // Card-as-action handler for RouteScreen. action is 'enroute' or
  // 'arrived'. Re-throws on error so the caller can render local
  // feedback. Refetch on success keeps the lifted feed in sync — the
  // claimed slice updates and the StatusPill on the card flips
  // through the next state without a manual refresh.
  const handleStateAction = useCallback(async (job, action) => {
    if (!session?.user?.id) throw new Error("SIGN_IN_REQUIRED");
    if (action === "enroute") {
      await markEnRoute({ requestId: job.id, driverUserId: session.user.id });
    } else if (action === "arrived") {
      await markArrived({ requestId: job.id, driverUserId: session.user.id });
    } else {
      throw new Error("UNKNOWN_ACTION:" + action);
    }
    await jobs.refetch();
  }, [session?.user?.id, jobs.refetch]);

  // Resolve selected job from any slice.
  const selectedJob = (() => {
    if (!selectedJobId) return null;
    const all = [...jobs.available, ...jobs.claimed, ...jobs.completed];
    return all.find(j => j.id === selectedJobId) || null;
  })();

  // Auth loading → minimal placeholder
  // visually enough; SharedLoginScreen takes over once we know the state).
  if (authLoading) {
    return (
      <div style={{
        minHeight: "100vh",
        background: LD.navy,
        color: LD.fog,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Sora',system-ui,sans-serif",
      }}>
        <div style={{ fontSize: 13, color: "var(--hl-text-muted)" }}>Loading...</div>
      </div>
    );
  }

  // Wrong-surface screen for residents who accidentally land on /haulers.
  // Renders before the SharedLoginScreen check so the residue session from
  // the in-flight sign-out doesn't cause a flash of the Hauler shell.
  if (wrongSurface) {
    return (
      <div style={{
        minHeight: "100vh",
        background: LD.navy,
        color: LD.fog,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Sora',system-ui,sans-serif",
        padding: 32,
        textAlign: "center",
      }}>
        <style>{SORA}</style>
        <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 12, color: "#fff" }}>
          This is the Hauler app
        </h1>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,.7)", lineHeight: 1.5, maxWidth: 420, marginBottom: 24 }}>
          You signed in with a customer account. Customers and Haulers use separate accounts at LoadDash. Head to the customer site to book a pickup.
        </p>
        <a
          href="/"
          style={{
            background: LD.sky,
            color: "#fff",
            textDecoration: "none",
            padding: "12px 24px",
            borderRadius: 10,
            fontWeight: 700,
            fontSize: 14,
            minHeight: 44,
            minWidth: 44,
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          Go to LoadDash customer site
        </a>
      </div>
    );
  }

  // Reset-password deep-link OR no session → SharedLoginScreen.
  // Shared login component; the surface prop swaps the
  // forgot-password redirectTo URL.
  if (passwordRecovery || !session) {
    return (
      <SharedLoginScreen
        surface="hauler"
        resetMode={passwordRecovery}
        onResetComplete={() => setPasswordRecovery(false)}
      />
    );
  }

  // Profile + Terms + IC still resolving → keep the loading placeholder.
  // Avoids a flash of the wrong gate while the three RPCs come back.
  if (driverProfile === undefined || termsAccepted === undefined || icSigned === undefined) {
    return (
      <div style={{
        minHeight: "100vh",
        background: LD.navy,
        color: LD.fog,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Sora',system-ui,sans-serif",
      }}>
        <div style={{ fontSize: 13, color: "var(--hl-text-muted)" }}>Loading...</div>
      </div>
    );
  }

  // First-login Hauler Terms gate. Recorded once in driver_agreement_log;
  // never shown again once accepted. The gate fires before the IC
  // Agreement so platform protections attach before legal commitment.
  if (!termsAccepted) {
    return (
      <HaulerTerms
        session={session}
        onComplete={() => setTermsAccepted(true)}
      />
    );
  }

  // Independent Contractor Agreement gate. record_driver_agreement writes
  // the ic-v1 row to driver_agreement_log and also flips
  // onboarding_status to 'approved'. The 5-step DriverOnboarding wizard
  // is gone — vehicle, bed, crew, trailer were captured at the apply
  // form. This is now a single-screen legal signature.
  if (!icSigned) {
    return (
      <DriverAgreement
        session={session}
        onComplete={() => setIcSigned(true)}
      />
    );
  }

  // Hauler availability indicator state. `null` while profile is
  // loading; otherwise the boolean is_available column. Tapping the
  // dot routes to the Profile tab.
  const isAvailable = driverProfile == null ? null : (driverProfile.is_available !== false);
  const goToProfileTab = () => setTab("profile");

  // Detail screen takes over the entire content area.
  if (selectedJob) {
    return (
      <ShellChrome isAvailable={isAvailable} onAvailabilityIndicatorTap={goToProfileTab}>
        {alerts.length > 0 && (
          <div style={{ maxWidth: 520, margin: "0 auto", padding: "10px 12px 0" }}>
            <CommunityAlertBanner alerts={alerts} audienceContext="driver" />
          </div>
        )}
        <main style={{ maxWidth: 520, margin: "0 auto" }}>
          <JobDetailScreen
            job={selectedJob}
            session={session}
            driverProfile={driverProfile}
            onBack={() => setSelectedJobId(null)}
            onChange={() => { jobs.refetch(); }}
          />
        </main>
      </ShellChrome>
    );
  }

  return (
    <ShellChrome isAvailable={isAvailable} onAvailabilityIndicatorTap={goToProfileTab}>
      {alerts.length > 0 && (
        <div style={{ maxWidth: 520, margin: "0 auto", padding: "10px 12px 0" }}>
          <CommunityAlertBanner alerts={alerts} audienceContext="driver" />
        </div>
      )}
      <main style={{ maxWidth: 520, margin: "0 auto" }}>
        {tab === "jobs" && (
          <JobsScreen
            session={session}
            driverProfile={driverProfile}
            available={jobs.available}
            claimed={jobs.claimed}
            completed={jobs.completed}
            loading={jobs.loading}
            error={jobs.error}
            onOpenJob={(job) => setSelectedJobId(job.id)}
            onClaim={handleInlineClaim}
            onRetry={jobs.refetch}
            viewerIsAdmin={isAdmin}
          />
        )}
        {tab === "route" && (
          <RouteScreen
            session={session}
            driverProfile={driverProfile}
            claimed={jobs.claimed}
            onOpenJob={(job) => setSelectedJobId(job.id)}
            onAction={handleStateAction}
          />
        )}
        {tab === "earnings" && (
          <EarningsScreen
            session={session}
            driverProfile={driverProfile}
            completed={jobs.completed}
          />
        )}
        {tab === "profile" && (
          <ProfileScreen
            session={session}
            driverProfile={driverProfile}
            onProfileUpdated={handleProfileUpdated}
            colorScheme={colorScheme}
          />
        )}
      </main>

      <BottomNav tab={tab} onChange={setTab} />
    </ShellChrome>
  );
}

function ShellChrome({ children, isAvailable, onAvailabilityIndicatorTap }) {
  // isAvailable: true | false | null (loading)
  // The indicator dot reflects the Hauler's `driver_profiles.is_available`
  // toggle. Tapping it routes to the Profile tab so they can flip without
  // navigating manually. IC-safe — purely informational, no copy implies
  // pressure either way.
  const showDot = isAvailable !== null && isAvailable !== undefined;
  return (
    <div style={{
      fontFamily: "'Sora', -apple-system, system-ui, sans-serif",
      minHeight: "100vh",
      background: LD.navy,
      color: LD.fog,
      WebkitFontSmoothing: "antialiased",
    }}>
      <style>{SORA}</style>

      <div style={{
        background: "var(--hl-top-bg)",
        padding: "10px 18px",
        paddingTop: "calc(10px + env(safe-area-inset-top, 0px))",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid var(--hl-top-border)",
        position: "sticky",
        top: 0,
        zIndex: 40,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="/Loaddashbutton.png" alt="LD" style={{ width: 24, height: 24, borderRadius: 6, objectFit: "contain" }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: LD.sky }}>
            LoadDash Haulers
          </span>
          {showDot && (
            <button
              type="button"
              onClick={onAvailabilityIndicatorTap}
              aria-label={isAvailable ? "You're available — tap to manage" : "Not available — tap to manage"}
              title={isAvailable ? "Available" : "Not available"}
              style={{
                marginLeft: 4,
                width: 22,
                height: 22,
                padding: 0,
                borderRadius: "50%",
                background: "transparent",
                border: "none",
                cursor: onAvailabilityIndicatorTap ? "pointer" : "default",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: isAvailable ? LD.green : "rgba(242,242,242,.35)",
                  boxShadow: isAvailable ? "0 0 8px rgba(3,166,106,.6)" : "none",
                }}
              />
            </button>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

function BottomNav({ tab, onChange }) {
  return (
    <nav style={{
      position: "fixed",
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 50,
      background: "var(--hl-nav-bg)",
      borderTop: "1px solid var(--hl-border)",
      padding: "9px 18px calc(12px + env(safe-area-inset-bottom, 0px))",
      boxShadow: "0 -12px 28px rgba(0,0,0,.18)",
    }}>
      <div style={{ maxWidth: 520, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
        {TABS.map(({ key, label }) => {
          const active = tab === key;
          const tabColor = active ? LD.sky : "var(--hl-nav-tab-inactive)";
          return (
            <button
              key={key}
              onClick={() => onChange(key)}
              style={{
                border: "none",
                background: "transparent",
                color: tabColor,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                fontFamily: "inherit",
                fontSize: 11,
                fontWeight: active ? 900 : 700,
                cursor: "pointer",
                padding: "2px 0 0",
                minHeight: 44,
                minWidth: 44,
              }}
            >
              <NavIcon type={key} color={tabColor} />
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
